import { useState } from 'react'
import { useWallet } from '../contexts/WalletContext'
import { useToast } from '../contexts/ToastContext'
import { formatCHNX } from '../utils/formatCHNX'

interface Duel {
  duelId: string
  creatorAddress: string
  creatorSide: 'sideA' | 'sideB'
  amount: string
}

interface Match {
  sideA: string
  sideB: string
}

interface MatchDuelModalProps {
  duel: Duel
  match: Match
  onClose: () => void
  onSubmit: (amount: string) => void
}

export default function MatchDuelModal({ duel, match, onClose, onSubmit }: MatchDuelModalProps) {
  const [amount, setAmount] = useState(duel.amount)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { balance } = useWallet()
  const toast = useToast()
  
  const requiredAmount = parseFloat(duel.amount)
  const availableBalance = balance ? parseFloat(balance) : 0
  const creatorSideName = duel.creatorSide === 'sideA' ? match.sideA : match.sideB
  const yourSide = duel.creatorSide === 'sideA' ? 'sideB' : 'sideA'
  const yourSideName = yourSide === 'sideA' ? match.sideA : match.sideB

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const amountNum = parseFloat(amount)
    
    // Validation
    if (!amount || isNaN(amountNum)) {
      toast.error('Please enter a valid amount')
      return
    }
    
    if (amountNum !== requiredAmount) {
      toast.warning(`You must predict with exact amount: ${requiredAmount} CHNX`)
      return
    }

    // Check wallet balance
    if (amountNum > availableBalance) {
      toast.error(`Insufficient balance! Your available balance is ${availableBalance.toFixed(2)} CHNX`)
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(amount)
      toast.success('Prediction matched successfully! Waiting for match result')
      onClose()
    } catch (error: any) {
      toast.error(error.message || 'Failed to match prediction, please try again')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Match Duel</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Duel Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Opponent Address</p>
              <p className="font-mono text-sm text-gray-900">
                {duel.creatorAddress.slice(0, 10)}...{duel.creatorAddress.slice(-8)}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Opponent predicting</p>
                <p className={`font-semibold ${
                  duel.creatorSide === 'sideA' ? 'text-blue-600' : 'text-red-600'
                }`}>
                  {creatorSideName}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">You will predict</p>
                <p className={`font-semibold ${
                  yourSide === 'sideA' ? 'text-blue-600' : 'text-red-600'
                }`}>
                  {yourSideName}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Required Amount</p>
              <p className="text-2xl font-bold text-gray-900">{requiredAmount} CHNX</p>
            </div>
          </div>

          {/* Amount Input (Fixed) */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Your Bet Amount (CHNX)
              </label>
              <span className="text-sm text-gray-600">
                Available: <span className="font-semibold text-green-600">{availableBalance.toFixed(2)} CHNX</span>
              </span>
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              step="0.00000001"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-50"
              readOnly
            />
            <p className="text-xs text-gray-500 mt-1">
              ⚠️ Amount must match exactly: {requiredAmount} CHNX
            </p>
          </div>

          {/* Potential Outcome */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-700">Total Pool:</span>
              <span className="font-semibold text-gray-900">
                {formatCHNX(requiredAmount * 2)} CHNX
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-700">If you win:</span>
              <span className="font-semibold text-green-600">
                +{formatCHNX(requiredAmount)} CHNX 🎉
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">If you lose:</span>
              <span className="font-semibold text-red-600">
                -{formatCHNX(requiredAmount)} CHNX 😢
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Matching...' : 'Confirm Match'}
            </button>
          </div>
        </form>

        {/* Info */}
        <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
          <p className="text-xs text-yellow-800 mb-2">
            ⚠️ <strong>Important:</strong> Once matched, your CHNX will be locked until the match 
            result is determined. Winner takes the entire pool!
          </p>
          <p className="text-xs text-yellow-700">
            📌 <strong>Next step:</strong> You will be asked to confirm the transfer to Escrow before matching the prediction.
          </p>
        </div>
      </div>
    </div>
  )
}

